import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import { useMentors } from "@/hooks/useMentors";
import TruncatedCredits from "@/components/TruncatedCredits";
import { Skeleton } from "@/components/ui/skeleton";

const defaultAvatar = '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png';

interface MentorListProps {
  onBookMentor?: (mentor: any) => void;
}

const MentorList = ({ onBookMentor }: MentorListProps) => {
  const navigate = useNavigate();
  const { mentors: hookMentors, loading, error } = useMentors();

  // Filter and sort mentors - exclude those without profile pictures
  // 1. BLAQOUT first always
  // 2. Alexander Solis (Alex "HITMAN" Solis) second
  // 3. Pro tier mentors, sorted by rating (highest first), then by total_sessions
  // 4. Non-pro tier mentors, sorted by rating (highest first), then by total_sessions
  // 5. If not rated, sort alphabetically
  const sortedMentors = [...hookMentors]
    .filter(mentor => mentor.profile_image_url && mentor.profile_image_url.trim() !== '')
    .sort((a, b) => {
    // BLAQOUT always comes first
    if (a.first_name === 'BLAQOUT') return -1;
    if (b.first_name === 'BLAQOUT') return 1;
    // Alexander Solis (Alex "HITMAN" Solis) second
    if (a.first_name === 'Alex "HITMAN"') return -1;
    if (b.first_name === 'Alex "HITMAN"') return 1;
    
    // Separate pro and non-pro tiers
    const aIsPro = a.tier === 'pro';
    const bIsPro = b.tier === 'pro';
    
    // Pro tier comes before non-pro tier
    if (aIsPro && !bIsPro) return -1;
    if (!aIsPro && bIsPro) return 1;
    
    // Within same tier, check if they have ratings
    const aHasRating = a.rating && a.rating > 0;
    const bHasRating = b.rating && b.rating > 0;
    
    // Both have ratings - sort by rating (highest first), then by total sessions
    if (aHasRating && bHasRating) {
      if (a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      return b.total_sessions - a.total_sessions;
    }
    
    // Only one has rating - rated mentor comes first
    if (aHasRating && !bHasRating) return -1;
    if (!aHasRating && bHasRating) return 1;
    
    // Neither has rating - sort alphabetically by first name
    return a.first_name.localeCompare(b.first_name);
    });

  const handleBookMentor = (mentor: any) => {
    if (onBookMentor) {
      onBookMentor(mentor);
    } else {
      navigate('/auth/student');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="obsidian-card p-4 flex gap-4">
            <Skeleton className="w-20 h-20 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
            </div>
            <div className="flex flex-col gap-2 w-24">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <p>Unable to load mentors. Please try again later.</p>
      </div>
    );
  }

  if (sortedMentors.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <p>No mentors available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedMentors.map((mentor) => (
        <div 
          key={mentor.id} 
          className="obsidian-card p-4 hover:border-primary/50 transition-all duration-300 flex flex-col md:flex-row gap-4 items-start md:items-center"
        >
          {/* Mentor Image */}
          <div className="relative flex-shrink-0">
            <div className="relative w-24 h-24 md:w-20 md:h-20 rounded-lg overflow-hidden">
              <img
                src={mentor.profile_image_url || defaultAvatar}
                alt={`${mentor.first_name} ${mentor.last_name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = defaultAvatar;
                }}
              />
              {mentor.discovery_call_enabled && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent py-0.5 text-center">
                  <span className="text-[0.6rem] font-bold text-primary-foreground tracking-wide">
                    FREE CONSULT
                  </span>
                </div>
              )}
            </div>
            {mentor.tier === 'pro' && (
              <div className="absolute -top-2 -right-2">
                <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} showTooltip={false} size="sm" />
              </div>
            )}
          </div>

          {/* Mentor Info - Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              {/* Left side - Name, Specialty, Credits */}
              <div className="flex-1 min-w-0">
                <div className="mb-2">
                  <h3 className="font-semibold text-lg text-foreground">
                    {mentor.first_name} {mentor.last_name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {mentor.specialty}
                  </p>
                </div>

                {/* Credits */}
                {mentor.credits && mentor.credits.length > 0 && (
                  <div className="mb-2">
                    <TruncatedCredits 
                      credits={mentor.credits} 
                      mentorId={mentor.id}
                      maxLines={2}
                    />
                  </div>
                )}

                {/* Rating */}
                {mentor.rating > 0 && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <Star className="w-4 h-4 fill-energy text-energy" />
                    <span className="font-semibold text-foreground">
                      {mentor.rating.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground">
                      ({mentor.total_sessions} sessions)
                    </span>
                  </div>
                )}
              </div>

              {/* Right side - Rate and Buttons */}
              <div className="flex flex-col items-start md:items-end gap-3 md:min-w-[200px]">
                {/* Hourly Rate */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {(mentor.hourly_rate / 100).toFixed(0)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    tokens/hour
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 w-full md:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 md:w-24"
                    onClick={() => navigate(`/mentor/${mentor.id}`)}
                  >
                    Profile
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 md:w-24 energy-button"
                    onClick={() => handleBookMentor(mentor)}
                  >
                    Book
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MentorList;
